import type { GearItem } from './items';

/**
 * Tempering (honing). Rules from the research (02 §3, §11.4):
 *   chance = min(1, base * (1 + 0.1 * min(fails, 10)) + catalysts * 0.1 * base)
 *   on failure: resolve += chance * 0.465; at resolve >= 1 the next attempt succeeds.
 * Weapons cost x1.6 and use weapon ore; armour uses armour ore.
 */

export interface TemperCost {
  shards: number;
  ore: number;
  leap: number;
  fusion: number;
  silver: number;
  gold: number;
}

export interface TemperStep {
  /** Target level (+1..+15). */
  plus: number;
  base: number;
  armor: TemperCost;
}

const row = (plus: number, base: number, ore: number, shards: number, leap: number, fusion: number, silver: number, gold: number): TemperStep => ({
  plus,
  base,
  armor: { ore, shards, leap, fusion, silver, gold },
});

/** +1 … +15 for endgame sets (research 02 §11.4). */
export const TEMPER_TABLE: TemperStep[] = [
  row(1, 1.0, 12, 40, 1, 0, 300, 0),
  row(2, 1.0, 14, 45, 1, 0, 300, 0),
  row(3, 1.0, 16, 50, 1, 0, 350, 0),
  row(4, 1.0, 18, 60, 2, 0, 400, 0),
  row(5, 1.0, 20, 70, 2, 0, 450, 0),
  row(6, 0.9, 24, 85, 2, 1, 500, 0),
  row(7, 0.8, 28, 100, 3, 1, 600, 0),
  row(8, 0.7, 32, 120, 3, 1, 700, 5),
  row(9, 0.6, 36, 140, 4, 2, 800, 10),
  row(10, 0.5, 40, 160, 4, 2, 900, 15),
  row(11, 0.4, 45, 190, 5, 2, 1000, 20),
  row(12, 0.3, 50, 220, 5, 3, 1100, 25),
  row(13, 0.2, 55, 260, 6, 3, 1250, 30),
  row(14, 0.15, 60, 300, 7, 4, 1400, 40),
  row(15, 0.1, 70, 350, 8, 4, 1600, 50),
];

export const WEAPON_COST_MULT = 1.6;
export const RESOLVE_GAIN = 0.465;
export const MAX_CATALYSTS = 10;

export function stepFor(g: GearItem): TemperStep | null {
  if (g.plus >= g.maxPlus) return null;
  return TEMPER_TABLE[g.plus] ?? null;
}

/**
 * Leveling gear (max +5) uses a cheap scaled-down version of the same table:
 * costs are scaled by the item's level relative to the endgame baseline.
 */
export function costFor(g: GearItem, step: TemperStep): TemperCost {
  const weapon = g.slot === 'weapon';
  const k = (weapon ? WEAPON_COST_MULT : 1) * (g.maxPlus >= 15 ? 1 : Math.max(0.15, g.baseIlvl / 250) * 0.6);
  const c = step.armor;
  return {
    shards: Math.round(c.shards * k),
    ore: Math.round(c.ore * k),
    leap: Math.round(c.leap * (weapon ? WEAPON_COST_MULT : 1) * (g.maxPlus >= 15 ? 1 : 0.5)),
    fusion: g.maxPlus >= 15 ? Math.round(c.fusion * (weapon ? WEAPON_COST_MULT : 1)) : 0,
    silver: Math.round(c.silver * k),
    gold: g.maxPlus >= 15 ? Math.round(c.gold * (weapon ? WEAPON_COST_MULT : 1)) : 0,
  };
}

export function temperChance(g: GearItem, catalysts = 0, tome = false): number {
  const step = stepFor(g);
  if (!step) return 0;
  if (g.resolve >= 1) return 1;
  const base = step.base * (tome && step.plus >= 11 ? 2 : 1);
  const juice = Math.min(catalysts, MAX_CATALYSTS) * 0.1 * base;
  return Math.min(1, base * (1 + 0.1 * Math.min(g.fails, 10)) + juice);
}

export interface TemperOutcome {
  success: boolean;
  chance: number;
  /** Resolve after the attempt (0..1). */
  resolve: number;
  plus: number;
}

/** Performs one attempt, mutating the gear's plus/fails/resolve. Costs are paid by the caller. */
export function temper(g: GearItem, rand: () => number, catalysts = 0, tome = false): TemperOutcome {
  const chance = temperChance(g, catalysts, tome);
  if (chance <= 0) return { success: false, chance: 0, resolve: g.resolve, plus: g.plus };
  if (rand() < chance) {
    g.plus++;
    g.fails = 0;
    g.resolve = 0;
    return { success: true, chance, resolve: 0, plus: g.plus };
  }
  g.fails++;
  g.resolve = Math.min(1, g.resolve + chance * RESOLVE_GAIN);
  return { success: false, chance, resolve: g.resolve, plus: g.plus };
}

/** Expected number of attempts for a step with pity (used by tooltips). */
export function expectedAttempts(base: number): number {
  let expected = 0;
  let pFail = 1;
  let resolve = 0;
  for (let fails = 0; fails < 200; fails++) {
    const c = resolve >= 1 ? 1 : Math.min(1, base * (1 + 0.1 * Math.min(fails, 10)));
    expected += (fails + 1) * pFail * c;
    pFail *= 1 - c;
    if (pFail < 1e-9) break;
    resolve = Math.min(1, resolve + c * RESOLVE_GAIN);
  }
  return expected;
}

/** Worst-case attempts (pity guarantee). */
export function pityAttempts(base: number): number {
  let resolve = 0;
  for (let fails = 0; fails < 200; fails++) {
    if (resolve >= 1) return fails + 1;
    const c = Math.min(1, base * (1 + 0.1 * Math.min(fails, 10)));
    if (c >= 1) return fails + 1;
    resolve = Math.min(1, resolve + c * RESOLVE_GAIN);
  }
  return 200;
}
